import type { Drawing } from "../../shared/art/types";

export const FLIGHT_VERSION = 2;
export const WORLD_WIDTH = 1440;
export const FIELD = {
  width: 720,
  height: 1280,
  hz: 60,
  shipMin: 72,
  shipMax: 112,
  bulletMin: 12,
  bulletMax: 24,
  core: 9,
} as const;
export type MainWeapon =
  | "straight"
  | "double"
  | "fan"
  | "laser"
  | "flame"
  | "rocket"
  | "rail"
  | "tesla";
export type SideWeapon =
  "wing" | "orbit" | "homing" | "boomerang" | "split" | "arc";
export type UpgradeId =
  | "attack"
  | "rate"
  | "critical"
  | "health"
  | "repair"
  | "shield"
  | "speed"
  | "experience"
  | "cooldown"
  | "grace"
  | "armor"
  | "regen"
  | "lanes"
  | "burst"
  | "pierce"
  | "size"
  | "velocity"
  | "push"
  | SideWeapon
  | "blast"
  | "chain"
  | "burn"
  | "frost"
  | "ricochet"
  | "magnet"
  | "overdrive"
  | "salvage"
  | "precision"
  | "execution"
  | "barrier"
  | "resonance"
  | `tune-${MainWeapon}`
  | `evolve-${MainWeapon}`;
export interface FlightDesign {
  id: string;
  revision: number;
  name: string;
  ship: Drawing;
  bullet: Drawing;
  shipSize: number;
  bulletSize: number;
  updatedAt: number;
  dirty?: boolean;
}
export interface FlightProfile {
  unlocked: SideWeapon[];
  solo: FlightRecord;
  coop: FlightRecord;
  todayStars: number;
}
export interface FlightRecord {
  ticks: number;
  bosses: number;
  level: number;
}
export interface PilotSeed {
  id: string;
  main: MainWeapon;
  unlocked: SideWeapon[];
}
export interface Pilot extends PilotSeed {
  x: number;
  y: number;
  tx: number;
  ty: number;
  hp: number;
  maxHp: number;
  shield: number;
  invulnerable: number;
  cooldown: number;
  fireAt: number;
  rescue: number;
  left: boolean;
  levels: Partial<Record<UpgradeId, number>>;
  choices: UpgradeId[];
  recentChoices?: UpgradeId[];
  rerolls?: number;
  overdriveUntil?: number;
  combo?: number;
  comboUntil?: number;
  damageDealt?: number;
  hitsTaken?: number;
  nextSide?: number;
  offeredWeapon?: MainWeapon;
}
export interface Enemy {
  id: number;
  kind: number;
  x: number;
  y: number;
  hp: number;
  maxHp: number;
  age: number;
  fireAt: number;
  phase: number;
  scale: number;
  boss: boolean;
  radius: number;
  altitude?: "air" | "ground";
  route?: "dive" | "sweep" | "orbit" | "pincer" | "ground";
  startY?: number;
  routeClock?: number;
  warnAt?: number;
  aim?: number;
  elite?: boolean;
  slowUntil?: number;
  burnUntil?: number;
  source?: string;
  startX?: number;
}
export interface Bullet {
  id: number;
  owner: string;
  kind: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  damage: number;
  life: number;
  pierce: number;
  hits: number[];
  warmup?: number;
  age?: number;
  splash?: number;
  turn?: number;
  previousX?: number;
  previousY?: number;
}
export interface Effect {
  id: number;
  x: number;
  y: number;
  age: number;
  kind:
    | "hit"
    | "burst"
    | "rescue"
    | "ink"
    | "chain"
    | "evolve"
    | "supply"
    | "hurt"
    | "boss";
  toX?: number;
  toY?: number;
  label?: string;
  target?: string;
}
export interface FlightState {
  version: 1 | 2;
  id: string;
  mode: "solo" | "coop";
  seed: number;
  rng: number;
  tick: number;
  nextId: number;
  phase: "playing" | "choosing" | "ended";
  pilots: Pilot[];
  enemies: Enemy[];
  bullets: Bullet[];
  effects: Effect[];
  level: number;
  xp: number;
  bosses: number;
  kills: number;
  nextBoss: number;
  spawnAt: number;
  scene: number;
  reason?: string;
  director?: {
    wave: number;
    event: "formation" | "elite" | "convoy" | "recovery";
    eventUntil: number;
    chapter: number;
    pressure: number;
    lastUpgrade: number;
    spawnSerial: number;
    killsAt: number;
    eventTarget: number;
    eventDone: boolean;
  };
  pickups?: {
    id: number;
    x: number;
    y: number;
    kind: "repair" | "shield" | "power" | "weapon";
    weapon?: MainWeapon;
    life: number;
  }[];
  banner?: { text: string; until: number };
}
export type FlightAction =
  | { kind: "move"; x: number; y: number }
  | { kind: "skill" }
  | { kind: "reroll" }
  | { kind: "swap" }
  | { kind: "choose"; upgrade: UpgradeId }
  | { kind: "leave" }
  | { kind: "end" };
export interface FlightCommand {
  tick: number;
  player: string;
  action: FlightAction;
}
export interface RunManifest {
  version: 1 | 2;
  id: string;
  seed: number;
  pilot: PilotSeed;
  design: FlightDesign;
  dependencies: string[];
}
export interface FlightChunk {
  index: number;
  until: number;
  commands: FlightCommand[];
  final: boolean;
}
export interface RunSave {
  manifest: RunManifest;
  state: FlightState;
  chunks: FlightChunk[];
  pending: FlightCommand[];
  acknowledged: number;
  status: "active" | "pending" | "verified" | "rejected";
  error?: string;
  earned?: number;
}
export interface FlightSeat {
  id: string;
  name: string;
  design: FlightDesign | null;
  main: MainWeapon;
  ready: boolean;
  remaining: number;
  connected: boolean;
}
export interface FlightRoom {
  id: string;
  code: string;
  host: string;
  public: boolean;
  seats: FlightSeat[];
  state: FlightState | null;
  choosingUntil: number;
  paused: boolean;
  message?: string;
}
export interface FlightRoomSummary {
  id: string;
  code: string;
  host: string;
  count: number;
}
